import * as React from 'react';

import { useEventCallback } from '@gitbook/util-react-hooks';

type CustomerIOActionHandlerProps = {
    actionName: string;
    onAction: (event: CustomerIoInAppMessageActionEvent) => void;
};

/**
 * Listen for a specific CustomerIO in-app action and invoke the provided callback.
 */
export function CustomerIOActionHandler(
    props: CustomerIOActionHandlerProps
): React.ReactElement | null {
    const { actionName, onAction } = props;
    const onCustomerIoAction = useEventCallback(onAction);

    React.useEffect(() => {
        const onMessageAction = (event: CustomerIoInAppMessageActionEvent) => {
            const triggeredActionIdentifiers = getCustomerIoActionIdentifiers(event);

            if (!triggeredActionIdentifiers.includes(actionName)) {
                return;
            }

            onCustomerIoAction(event);
        };

        let customerIoClient: CustomerIoClient | null = null;
        let attachInterval: number | null = null;

        /**
         * Attach the CustomerIO action listener once the SDK is available.
         * The snippet initialises window._cio as a stub array while we wait for
         * the real SDK object.
         */
        const attachListener = () => {
            if (customerIoClient) {
                return true;
            }

            const cio = window._cio as unknown as CustomerIoClient | undefined;
            if (!cio?.on || Array.isArray(window._cio)) {
                return false;
            }

            customerIoClient = cio;
            customerIoClient.on('in-app:message-action', onMessageAction);
            return true;
        };

        if (!attachListener()) {
            attachInterval = window.setInterval(() => {
                if (!attachListener() || !attachInterval) {
                    return;
                }

                window.clearInterval(attachInterval);
                attachInterval = null;
            }, 250);
        }

        return () => {
            if (attachInterval) {
                window.clearInterval(attachInterval);
            }

            // Unregister the listener when the component unmounts. We call `.off` on
            // `window._cio` here rather than using `customerIoClient` in case the SDK
            // replaced the object we captured when attaching.
            window._cio?.off?.('in-app:message-action', onMessageAction);
        };
    }, [actionName, onCustomerIoAction]);

    return null;
}

function getCustomerIoActionIdentifiers(event: CustomerIoInAppMessageActionEvent): string[] {
    return [
        event.detail?.actionName,
        event.detail?.actionValue,
        event.action?.name,
        event.action?.value,
        event.detail?.action?.name,
        event.detail?.action?.value,
    ].filter((value): value is string => Boolean(value));
}
